/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { EventChannel, eventChannel } from 'redux-saga';
import { call, fork, ForkEffect, put, select, take, takeLatest } from 'redux-saga/effects';

import {
  Coordinates,
  selectActiveHorsesField,
  selectCameraMode,
  selectCameraPosition,
  selectFollowZoomStops,
  selectHorsesBehindGoal,
  selectHorsesStarted,
  selectHurdles,
  selectIsGameRunning,
  selectIsHistoryMode,
  selectMoves,
  selectPanelState,
  selectRound,
} from '~/app';
import { setCurrentState } from '~/game';
import { setChatPanelState, setHistoryPanelState } from '~/overlay/actions';

import {
  HorseField,
  moveCamera,
  MoveCameraPayload,
  requestInitialCamera,
  setCameraMode,
  setFollowZoomStops,
  setHorsesField,
  setHurdles,
  setMoves,
} from './actions';
import { recordCameraSagaEmit, recordCameraSagaSkipped } from './cameraDebug';
import {
  adaptiveMarginFraction,
  applyZoomOutGuardrail,
  computeDynamicInsets,
  computeFocusPoint,
  computeRawFitScale,
} from './cameraFit';
import {
  bboxFromPoints,
  CameraPoiContext,
  hasHardPoiConstraint,
  resolveCameraPois,
  unionBoundingBoxes,
} from './cameraPoi';
import { bootstrapScaleFromFollowStops, computeFollowScaleFromFit, shouldSkipCameraRetarget } from './cameraZoom';

/** Action type used internally to trigger camera recalculation on window resize */
const WINDOW_RESIZE = 'camera/WINDOW_RESIZE';

/** Minimum bbox dimension (board-normalized view coords). */
const MIN_CONTENT_SIZE = 0.105;

function* putCameraIfChanged(payload: MoveCameraPayload) {
  const current: { x: number; y: number; scale: number } = yield select(selectCameraPosition);
  if (shouldSkipCameraRetarget(current, payload)) {
    recordCameraSagaSkipped();
    return;
  }
  recordCameraSagaEmit();
  yield put(moveCamera(payload));
}

function createResizeChannel(): EventChannel<boolean> {
  return eventChannel((emit) => {
    const handler = () => emit(true);
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handler);
      }
    };
  });
}

function* watchResize(): Generator<unknown, void, EventChannel<boolean>> {
  const channel = yield call(createResizeChannel);
  try {
    while (true) {
      yield take(channel);
      yield put({ type: WINDOW_RESIZE });
    }
  } finally {
    channel.close();
  }
}

function collectBasePoints(
  horsesField: Coordinates[],
  moves: Coordinates[][],
): Coordinates[] {
  const points: Coordinates[] = [...horsesField];

  moves.forEach((move) => {
    if (move && move.length > 0) {
      points.push(move[move.length - 1]);
    }
  });

  return points;
}

function activeHorseProgressValues(horseFields: HorseField[]): number[] {
  return horseFields.map((f) => f.prog ?? 0);
}

/**
 * Main camera saga: calculates camera position and zoom based on content, POIs, and panel state.
 */
function* cameraSaga() {
  const isGameRunning: boolean = yield select(selectIsGameRunning);
  if (!isGameRunning) {
    const followZoomStops: number = yield select(selectFollowZoomStops);
    const fallbackScale = bootstrapScaleFromFollowStops(followZoomStops);
    yield* putCameraIfChanged({
      x: 0.5,
      y: 0.5,
      scale: fallbackScale,
    });
    return;
  }

  const cameraMode: string = yield select(selectCameraMode);
  if (cameraMode === 'fixed') {
    return;
  }

  const horseFieldsFull: HorseField[] = yield select(selectActiveHorsesField);
  const horsesField: Coordinates[] = horseFieldsFull;
  const isHistoryMode: boolean = yield select(selectIsHistoryMode);
  const moves: Coordinates[][] = isHistoryMode ? [] : [...(yield select(selectMoves))];
  const round: number = yield select(selectRound);
  const hurdles = yield select(selectHurdles);
  const horsesStarted: boolean[] = yield select(selectHorsesStarted);
  const horsesBehindGoal: boolean[] = yield select(selectHorsesBehindGoal);

  const basePoints = collectBasePoints(horsesField, moves);
  let bbox = bboxFromPoints(basePoints, MIN_CONTENT_SIZE);

  const poiContext: CameraPoiContext = {
    round,
    activeHorseProgress: activeHorseProgressValues(horseFieldsFull),
    hurdles,
    hurdleVisibility: { round, horsesStarted, horsesBehindGoal },
  };
  const pois = resolveCameraPois(poiContext);
  const relaxGuardrail = hasHardPoiConstraint(pois);

  for (const poi of pois) {
    const poiBbox = bboxFromPoints(poi.points, MIN_CONTENT_SIZE);
    bbox = unionBoundingBoxes(bbox, poiBbox, MIN_CONTENT_SIZE);
  }

  if (!bbox) {
    yield* putCameraIfChanged({
      x: 0.5,
      y: 0.5,
      scale: 1.0,
    });
    return;
  }

  const panelState = yield select(selectPanelState);
  const insets = computeDynamicInsets(panelState);

  const marginFraction = adaptiveMarginFraction(bbox);
  let rawFitScale = computeRawFitScale(bbox, insets, marginFraction);
  rawFitScale = applyZoomOutGuardrail(rawFitScale, insets, relaxGuardrail);

  const followZoomStops: number = yield select(selectFollowZoomStops);
  const scale = computeFollowScaleFromFit(rawFitScale, followZoomStops);

  const focusPoint = computeFocusPoint(bbox, insets, scale);

  yield* putCameraIfChanged({
    x: focusPoint.x,
    y: focusPoint.y,
    scale,
  });
}

function* cameraSagaRoot(): Generator<ForkEffect, void, unknown> {
  yield fork(watchResize);
  yield takeLatest(
    [
      requestInitialCamera.type,
      setHorsesField.type,
      setMoves.type,
      setHurdles.type,
      setCurrentState.type,
      setHistoryPanelState.type,
      setChatPanelState.type,
      setCameraMode.type,
      setFollowZoomStops.type,
      WINDOW_RESIZE,
    ],
    cameraSaga,
  );
}

export default cameraSagaRoot;

// Re-export for tests
export { collectBasePoints, MIN_CONTENT_SIZE };
